import React, { useState, useEffect, useMemo, useRef } from 'react'
import { toast } from 'react-hot-toast'
import {
  X,
  Eye,
  MoreHorizontal,
  Trash2,
  Edit,
  Paperclip,
  Plus,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Calendar,
  Pin,
  Sparkles,
  Zap,
  CheckCircle2,
  Circle,
  ExternalLink,
  Copy,
  Check,
  FileText,
  Image as ImageIcon,
  Download,
  Link as LinkIcon,
  Clock,
  ArrowRight,
  Share2,
  Settings,
  SlidersHorizontal,
  User as UserIcon,
  CheckSquare,
  Flag,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '../../../shared/components/ui/Button'
import { IssueTypeIcon } from './ListRow'
import { TaskCustomFieldsSection } from './TaskCustomFieldsSection'
import { CreateFieldModal } from './CreateFieldModal'
import { PRIORITY_ICON, PRIORITY_OPTIONS as PRIORITIES, chipToneFor } from '../boardUtils'
import { useUser } from '../../../shared/hooks/useAuth'
import { useSpace, useIssueDetail, useIssueComments, useAddComment, useIssueWatchers, useAddWatcher, useRemoveWatcher, useIssueHistory, useIssueWorklog, useAddWorklog, useUpdateTimeTracking, useIssues } from '../hooks'
import { useQueryClient } from '@tanstack/react-query'
import { TimeTrackingModal, formatHoursToShortString } from './TimeTrackingModal'
import {
  createIssue,
  updateIssue,
  deleteIssue,
  addIssueAttachment,
  deleteIssueAttachment,
  createTaskDependency,
  deleteTaskDependency,
  listIssues,
  getIssueErrorMessage,
  flagIssue,
  createCustomField,
} from '../api'
import type { Issue, WorkflowStatus, IssueComment, IssueAttachment, IssueDependency } from '../api'
import { useUserOptions, type UserOption } from '../../../shared/hooks/useUserOptions'
import { canonicalDepartmentName } from '@jira-clone/shared-types'

export interface IssueModalProps {
  issue: Issue | null
  open: boolean
  onClose: () => void
  statuses: WorkflowStatus[]
  members?: (UserOption | any)[]
  epics?: Issue[]
  departments?: string[]
  onUpdate: (id: string, payload: Partial<Issue>) => Promise<void> | void
  onDelete: (id: string) => Promise<void> | void
}

const QUICK_COMMENTS = [
  '🎉 Looks good!',
  '🤝 Need help?',
  '⛔ This is blocked...',
  '🔍 Can you clarify...?',
]

export function formatDetailDate(dStr?: string | null): string {
  if (!dStr) return 'July 22, 2026 at 8:25 PM'
  const d = new Date(dStr)
  if (isNaN(d.getTime())) return dStr
  const datePart = d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${datePart} at ${timePart}`
}

export function getStatusBadgeStyle(category?: string, name?: string): string {
  const cat = (category || '').toLowerCase()
  const n = (name || '').toLowerCase()
  if (cat === 'done' || n.includes('done') || n.includes('completed')) {
    return 'bg-[#E3FCEF] text-[#006644] hover:bg-[#D3F5E4] border border-[#ABF5D1]'
  }
  if (cat === 'in_progress' || n.includes('progress') || n.includes('review')) {
    return 'bg-[#DEEBFF] text-[#0052CC] hover:bg-[#B3D4FF] border border-[#B3D4FF]'
  }
  return 'bg-[#EBECF0] text-[#42526E] hover:bg-[#DFE1E6] border border-[#DFE1E6]'
}

function formatBytes(bytes?: number): string {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const DEPARTMENT_OPTIONS = ['Development', 'Creative & Marketing']

function getDepartmentTeamName(dept?: string | null): string {
  if (!dept) return 'Development'
  const d = dept.trim()
  if (/marketing/i.test(d)) return 'Creative & Marketing'
  if (/develop/i.test(d) || /engineer/i.test(d)) return 'Development'
  if (/creat/i.test(d) || /design/i.test(d)) return 'Creative & Marketing'
  if (/sales/i.test(d)) return 'Sales'
  if (/hr/i.test(d) || /human/i.test(d)) return 'HR'
  if (/account/i.test(d) || /finance/i.test(d)) return 'Accounts'
  if (/superadmin/i.test(d) || /admin/i.test(d)) return 'Development'
  return d
}

function matchesDepartment(
  user: { department?: string | null; role?: string | null },
  targetDept: string,
): boolean {
  if (!targetDept) return true
  const role = (user.role || '').toLowerCase()
  if (role === 'superadmin' || role === 'owner') return true

  const uDept = String(canonicalDepartmentName(user.department)).toLowerCase()
  const tDept = canonicalDepartmentName(targetDept).toLowerCase()

  if (tDept.includes('creat') || tDept.includes('design')) {
    return uDept.includes('creat') || uDept.includes('design')
  }
  if (tDept.includes('develop') || tDept.includes('tech') || tDept.includes('engineer')) {
    return uDept.includes('develop') || uDept.includes('tech') || uDept.includes('engineer')
  }
  return uDept.includes(tDept) || tDept.includes(uDept)
}

export function IssueModal({
  issue,
  open,
  onClose,
  statuses,
  members,
  epics = [],
  departments = [],
  onUpdate,
  onDelete,
}: IssueModalProps) {
  const currentUser = useUser()
  const spaceId = issue?.spaceId || (issue as any)?.projectId || null
  const { data: spaceIssues = [] } = useIssues(open && spaceId ? spaceId : null)

  // Real-time backend queries
  const issueDetailQuery = useIssueDetail(issue?.id ?? null)
  const commentsQuery = useIssueComments(issue?.id ?? null)
  const addCommentMutation = useAddComment(issue?.id ?? null)
  const worklogQuery = useIssueWorklog(open && issue?.id ? issue.id : null)
  const historyQuery = useIssueHistory(open && issue?.id ? issue.id : null)
  const addWorklogMutation = useAddWorklog(issue?.id ?? null)
  const updateTimeTrackingMutation = useUpdateTimeTracking(issue?.id ?? null)
  const worklogs = worklogQuery.data || []
  const [timeTrackingModalOpen, setTimeTrackingModalOpen] = useState(false)

  // Local state initialized from issue
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState('')
  const [priority, setPriority] = useState<Issue['priority']>('medium')
  const [assigneeId, setAssigneeId] = useState('')
  const [reporterId, setReporterId] = useState('')
  const [parentId, setParentId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [startDate, setStartDate] = useState('')
  const [storyPoints, setStoryPoints] = useState<string>('')
  const [labels, setLabels] = useState<string[]>([])
  const [newLabelInput, setNewLabelInput] = useState('')
  const [team, setTeam] = useState('Development')

  // UI state
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const [detailsExpanded, setDetailsExpanded] = useState(true)
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true)
  const [subtasksExpanded, setSubtasksExpanded] = useState(true)
  const [linkedExpanded, setLinkedExpanded] = useState(true)
  const [activityTab, setActivityTab] = useState<'all' | 'comments' | 'history' | 'worklog'>('comments')
  const [commentText, setCommentText] = useState('')
  const commentInputRef = useRef<HTMLTextAreaElement>(null)

  // Custom field creation modal
  const [showCreateFieldModal, setShowCreateFieldModal] = useState(false)

  const { data: space } = useSpace(spaceId || null)
  const currentOrgId = (space as any)?.organizationId || (issue as any)?.organizationId || null
  const queryClient = useQueryClient()
  const [subtasks, setSubtasks] = useState<{ id: string; title: string; done: boolean }[]>([])
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const [showSubtaskInput, setShowSubtaskInput] = useState(false)
  const [subtaskSaving, setSubtaskSaving] = useState(false)
  const [localComments, setLocalComments] = useState<IssueComment[]>([])
  const [commentSaving, setCommentSaving] = useState(false)

  // Press M to comment keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'm' || e.key === 'M') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const activeTag = (document.activeElement?.tagName || '').toLowerCase()
        if (activeTag !== 'input' && activeTag !== 'textarea') {
          e.preventDefault()
          setActivityTab('comments')
          setTimeout(() => {
            commentInputRef.current?.focus()
          }, 50)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Modal action mode tabs
  const [actionMode, setActionMode] = useState<'view' | 'edit' | 'delete'>('view')
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  // Attachments state
  const [attachments, setAttachments] = useState<IssueAttachment[]>([])
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Linked items state
  const [linkedItems, setLinkedItems] = useState<
    Array<{
      id: string
      relation: string
      targetKey: string
      targetTitle: string
      targetId: string
    }>
  >([])
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [linkRelationType, setLinkRelationType] = useState<'FINISH_TO_START' | 'START_TO_FINISH' | 'START_TO_START'>('FINISH_TO_START')
  const [selectedLinkTargetId, setSelectedLinkTargetId] = useState('')
  const [availableSiblingTasks, setAvailableSiblingTasks] = useState<Issue[]>([])
  const [loadingSiblings, setLoadingSiblings] = useState(false)
  const [linkingTask, setLinkingTask] = useState(false)

  // Work log state
  const [loggedHours, setLoggedHours] = useState<string>('0')
  const [hoursToAdd, setHoursToAdd] = useState('')

  // Development section
  const [devSectionOpen, setDevSectionOpen] = useState(false)
  const [copiedBranch, setCopiedBranch] = useState(false)

  // Automation section
  const [autoSectionOpen, setAutoSectionOpen] = useState(false)

  const moreMenuRef = useRef<HTMLDivElement>(null)
  const statusMenuRef = useRef<HTMLDivElement>(null)

  // Sync state whenever the issue changes
  useEffect(() => {
    if (issue) {
      setTitle(issue.title || '')
      setDescription(issue.description || '')
      const rawSt =
        typeof issue.status === 'object' && issue.status !== null
          ? String((issue.status as any).id || (issue.status as any).name || '')
          : String(issue.status || '')
      setStatus(rawSt)
      const rawPrio =
        typeof issue.priority === 'object' && issue.priority !== null
          ? String((issue.priority as any).value || (issue.priority as any).name || 'medium')
          : String(issue.priority || 'medium')
      setPriority(rawPrio as Issue['priority'])
      setAssigneeId(issue.assignee_id || '')
      setReporterId((issue as any)?.reporter_id || '')
      setParentId(issue.parent_id || '')
      setDueDate(issue.due_date ? String(issue.due_date).slice(0, 10) : '')
      setStartDate(
        (issue as any).start_date
          ? String((issue as any).start_date).slice(0, 10)
          : (issue as any).startDate
          ? String((issue as any).startDate).slice(0, 10)
          : '',
      )
      setTeam(issue.team || (issue as any)?.customFieldValues?.team || 'Development')
      setStoryPoints(
        issue.story_points
          ? String(issue.story_points)
          : issue.estimatedHours
          ? String(issue.estimatedHours)
          : '',
      )
      setLoggedHours(String((issue as any).loggedHours || '0'))
      setLabels(issue.tags ?? issue.labels ?? [])
      setShowMoreMenu(false)
      setStatusDropdownOpen(false)
      setShowSubtaskInput(false)
      setShowLinkModal(false)
      setCommentText('')
    }
  }, [issue])

  // Sync subtasks from detail query or fallback
  useEffect(() => {
    if (issueDetailQuery.data?.subtasks) {
      setSubtasks(
        issueDetailQuery.data.subtasks.map((s: Issue) => ({
          id: s.id,
          title: s.title,
          done: s.status_category === 'done' || s.status === 'done',
        })),
      )
    }
  }, [issueDetailQuery.data])

  // Sync attachments from detail query or issue
  useEffect(() => {
    const atts = issueDetailQuery.data?.attachments ?? issue?.attachments ?? []
    setAttachments(atts)
  }, [issueDetailQuery.data?.attachments, issue?.attachments])

  // Sync linked items from detail query or issue
  useEffect(() => {
    const deps = issueDetailQuery.data?.dependencies ?? issue?.dependencies ?? []
    const depts = issueDetailQuery.data?.dependents ?? issue?.dependents ?? []
    const list: Array<{
      id: string
      relation: string
      targetKey: string
      targetTitle: string
      targetId: string
    }> = []

    deps.forEach((d: any) => {
      const tgt = d.targetTask || {}
      list.push({
        id: d._id || d.id,
        relation:
          d.type === 'FINISH_TO_START'
            ? 'Blocks'
            : d.type === 'START_TO_FINISH'
            ? 'Is blocked by'
            : 'Relates to',
        targetKey: tgt.number ? `TASK-${tgt.number}` : 'Item',
        targetTitle: tgt.title || 'Linked task',
        targetId: d.targetTaskId,
      })
    })

    depts.forEach((d: any) => {
      const src = d.sourceTask || {}
      list.push({
        id: d._id || d.id,
        relation: 'Is blocked by',
        targetKey: src.number ? `TASK-${src.number}` : 'Item',
        targetTitle: src.title || 'Linked task',
        targetId: d.sourceTaskId,
      })
    })

    setLinkedItems(list)
  }, [issueDetailQuery.data, issue])

  // Sync comments
  const allComments = useMemo(() => {
    const fetched = commentsQuery.data ?? issueDetailQuery.data?.comments ?? []
    const combined = [...fetched, ...localComments]
    // deduplicate by id
    const seen = new Set<string>()
    return combined.filter((c) => {
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return true
    })
  }, [commentsQuery.data, issueDetailQuery.data, localComments])

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false)
      }
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Save changes to database
  const saveField = async (payload: Partial<Issue> & Record<string, any>) => {
    if (!issue) return
    try {
      await onUpdate(issue.id, payload)
    } catch (err) {
      console.error('Failed to update issue field', err)
      toast.error(getIssueErrorMessage(err, 'update this task'))
    }
  }

  // Handle title blur
  const handleTitleBlur = () => {
    if (!issue) return
    if (title.trim() && title !== issue.title) {
      saveField({ title: title.trim() })
    }
  }

  // Handle description blur
  const handleDescriptionBlur = () => {
    if (!issue) return
    if (description !== (issue.description || '')) {
      saveField({ description })
    }
  }

  // Handle status selection
  const handleStatusSelect = (statusId: string) => {
    setStatus(statusId)
    setStatusDropdownOpen(false)
    const st = statuses.find((s) => s.id === statusId)
    saveField({
      status: statusId,
      status_category: st?.category ?? 'todo',
    })
  }

  // Handle priority selection
  const handlePrioritySelect = (p: Issue['priority']) => {
    setPriority(p)
    saveField({ priority: p })
  }

  // Handle assignee selection
  const handleAssigneeSelect = (uId: string) => {
    setAssigneeId(uId)
    saveField({ assignee_id: uId || null })
  }

  // Handle reporter selection
  const handleReporterSelect = (rId: string) => {
    setReporterId(rId)
    saveField({ reporter_id: rId || null, reporterId: rId || null } as any)
  }

  // Handle epic selection
  const handleParentSelect = (pId: string) => {
    setParentId(pId)
    saveField({ parent_id: pId || null })
  }

  // Handle due date change
  const handleDueDateChange = (val: string) => {
    setDueDate(val)
    saveField({ due_date: val ? new Date(val).toISOString() : null })
  }

  // Handle start date change
  const handleStartDateChange = (val: string) => {
    setStartDate(val)
    saveField({
      start_date: val ? new Date(val).toISOString() : null,
      startDate: val ? new Date(val).toISOString() : null,
    } as any)
  }

  // Handle team blur
  const handleTeamBlur = () => {
    saveField({ team, customFieldValues: { team } } as any)
  }

  // Handle story points change
  const handleStoryPointsBlur = () => {
    const num = storyPoints ? parseFloat(storyPoints) : undefined
    saveField({ story_points: num, estimatedHours: num })
  }

  // Handle delete
  const handleDeleteIssue = async () => {
    if (!issue) return
    if (confirm(`Are you sure you want to delete issue ${issue.key}?`)) {
      try {
        await onDelete(issue.id)
        onClose()
      } catch (err) {
        toast.error(getIssueErrorMessage(err, 'delete this task'))
      }
    }
  }

  // Handle file upload for attachments
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !issue) return
    setUploadingAttachment(true)

    try {
      const reader = new FileReader()
      reader.onload = async () => {
        const dataUrl = reader.result as string
        try {
          const res = await addIssueAttachment(issue.id, {
            name: file.name,
            url: dataUrl,
            size: file.size,
            type: file.type,
          })
          setAttachments((prev) => [
            ...prev,
            res || {
              id: `att-${Date.now()}`,
              name: file.name,
              url: dataUrl,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
            },
          ])
        } catch (err) {
          toast.error(getIssueErrorMessage(err, 'upload attachments'))
        }
      }
      reader.readAsDataURL(file)
    } catch (err) {
      console.error('File read error:', err)
    } finally {
      setUploadingAttachment(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // Delete attachment
  const handleDeleteAttachment = async (attId: string) => {
    if (!issue) return
    const previous = attachments
    setAttachments(attachments.filter((a) => a.id !== attId))
    try {
      await deleteIssueAttachment(issue.id, attId)
    } catch (err) {
      setAttachments(previous)
      toast.error(getIssueErrorMessage(err, 'delete attachments'))
    }
  }

  // Create custom field handler
  const handleCreateCustomField = async (payload: {
    name: string
    type: string
    config?: Record<string, any>
    isRequired?: boolean
  }) => {
    if (!currentOrgId) {
      toast.error('No organization context found.')
      return
    }
    try {
      await createCustomField({
        ...payload,
        projectId: spaceId || undefined,
        organizationId: currentOrgId,
      })
      toast.success('Field created.')
      setShowCreateFieldModal(false)
      queryClient.invalidateQueries({ queryKey: ['task-custom-field-values', issue?.id] })
    } catch (err: any) {
      toast.error(err?.message || 'Could not create field.')
    }
  }

  // Handle add subtask
  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newSubtaskTitle.trim() || !issue) return
    const titleVal = newSubtaskTitle.trim()
    setSubtaskSaving(true)
    try {
      const created = await createIssue(issue.spaceId, {
        title: titleVal,
        parent_id: issue.id,
        type: 'subtask',
        status: 'todo',
      })
      setSubtasks((prev) => [
        ...prev,
        { id: created.id, title: created.title, done: false },
      ])
      setNewSubtaskTitle('')
      setShowSubtaskInput(false)
      issueDetailQuery.refetch()
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'add subtasks'))
    } finally {
      setSubtaskSaving(false)
    }
  }

  // Toggle subtask done
  const toggleSubtask = async (subId: string) => {
    const target = subtasks.find((s) => s.id === subId)
    if (!target) return
    const nextDone = !target.done
    const previous = subtasks
    setSubtasks((prev) =>
      prev.map((s) => (s.id === subId ? { ...s, done: nextDone } : s)),
    )
    try {
      await updateIssue(subId, {
        status: nextDone ? 'done' : 'todo',
        status_category: nextDone ? 'done' : 'todo',
      })
      issueDetailQuery.refetch()
    } catch (err) {
      setSubtasks(previous)
      toast.error(getIssueErrorMessage(err, 'update subtasks'))
    }
  }

  // Delete subtask
  const handleDeleteSubtask = async (subId: string) => {
    const previous = subtasks
    setSubtasks((prev) => prev.filter((s) => s.id !== subId))
    try {
      await deleteIssue(subId)
      issueDetailQuery.refetch()
    } catch (err) {
      setSubtasks(previous)
      toast.error(getIssueErrorMessage(err, 'delete subtasks'))
    }
  }

  // Open add link dialog and load siblings
  const handleOpenAddLink = async () => {
    if (!issue?.spaceId) return
    setShowLinkModal(true)
    setLoadingSiblings(true)
    try {
      const all = await listIssues(issue.spaceId)
      const siblings = all.filter((i) => i.id !== issue.id)
      setAvailableSiblingTasks(siblings)
      if (siblings.length > 0) {
        setSelectedLinkTargetId(siblings[0].id)
      }
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'load related tasks'))
    } finally {
      setLoadingSiblings(false)
    }
  }

  // Submit link creation
  const handleCreateLink = async () => {
    if (!issue || !selectedLinkTargetId) return
    setLinkingTask(true)
    try {
      const created = await createTaskDependency(
        issue.id,
        selectedLinkTargetId,
        linkRelationType,
      )
      const targetTask = availableSiblingTasks.find((i) => i.id === selectedLinkTargetId)
      setLinkedItems((prev) => [
        ...prev,
        {
          id: created?.id || `dep-${Date.now()}`,
          relation:
            linkRelationType === 'FINISH_TO_START'
              ? 'Blocks'
              : linkRelationType === 'START_TO_FINISH'
              ? 'Is blocked by'
              : 'Relates to',
          targetKey: targetTask?.key || 'TASK',
          targetTitle: targetTask?.title || 'Linked task',
          targetId: selectedLinkTargetId,
        },
      ])
      setShowLinkModal(false)
      issueDetailQuery.refetch()
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'link tasks'))
    } finally {
      setLinkingTask(false)
    }
  }

  // Delete link
  const handleDeleteLink = async (linkId: string) => {
    const previous = linkedItems
    setLinkedItems((prev) => prev.filter((l) => l.id !== linkId))
    try {
      await deleteTaskDependency(linkId)
      issueDetailQuery.refetch()
    } catch (err) {
      setLinkedItems(previous)
      toast.error(getIssueErrorMessage(err, 'delete links'))
    }
  }

  // Handle add comment
  const handleSaveComment = async () => {
    if (!commentText.trim()) return
    setCommentSaving(true)
    const text = commentText.trim()
    try {
      const created = await addCommentMutation.mutateAsync(text)
      setLocalComments((prev) => [...prev, created as IssueComment])
      setCommentText('')
      commentsQuery.refetch()
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'add comments'))
    } finally {
      setCommentSaving(false)
    }
  }

  // Add label
  const handleAddLabel = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newLabelInput.trim()) {
      e.preventDefault()
      const updated = [...labels, newLabelInput.trim()]
      setLabels(updated)
      setNewLabelInput('')
      saveField({ labels: updated, tags: updated })
    }
  }

  const handleRemoveLabel = (idx: number) => {
    const updated = labels.filter((_, i) => i !== idx)
    setLabels(updated)
    saveField({ labels: updated, tags: updated })
  }

  // Time tracking calculations
  const currentSpentHours = useMemo(() => {
    if (issue?.timeSpent != null && issue.timeSpent > 0) return issue.timeSpent
    const parsedLogged = parseFloat(loggedHours)
    if (!isNaN(parsedLogged) && parsedLogged > 0) return parsedLogged
    if ((issue as any)?.loggedHours != null) return (issue as any).loggedHours
    return 0
  }, [issue?.timeSpent, (issue as any)?.loggedHours, loggedHours])

  const estimatedTotalHours = useMemo(() => {
    const p = parseFloat(storyPoints)
    if (!isNaN(p) && p > 0) return p
    if (issue?.estimatedHours && issue.estimatedHours > 0) return issue.estimatedHours
    return 0
  }, [storyPoints, issue?.estimatedHours])

  const currentRemainingHours = useMemo(() => {
    if ((issue as any)?.timeRemaining != null) return (issue as any).timeRemaining
    if (estimatedTotalHours > 0) {
      return Math.max(0, Math.round((estimatedTotalHours - currentSpentHours) * 100) / 100)
    }
    return null
  }, [(issue as any)?.timeRemaining, estimatedTotalHours, currentSpentHours])

  const progressPercent = useMemo(() => {
    if (estimatedTotalHours <= 0) return currentSpentHours > 0 ? 100 : 0
    return Math.min(100, Math.round((currentSpentHours / estimatedTotalHours) * 100))
  }, [currentSpentHours, estimatedTotalHours])

  const handleSaveTimeTracking = async (timeSpent: number, timeRemaining: number | null) => {
    if (!issue?.id) return
    try {
      await updateTimeTrackingMutation.mutateAsync({
        timeSpent,
        timeRemaining,
      })
      const prev = currentSpentHours
      if (timeSpent > prev) {
        try {
          await addWorklogMutation.mutateAsync({
            hours: Math.round((timeSpent - prev) * 100) / 100,
            comment: 'Time tracking update',
          })
        } catch {
          // non-blocking
        }
      } else if (timeSpent > 0 && worklogs.length === 0) {
        try {
          await addWorklogMutation.mutateAsync({
            hours: timeSpent,
            comment: 'Initial time tracking log',
          })
        } catch {
          // non-blocking
        }
      }
      setLoggedHours(String(timeSpent))
      saveField({ loggedHours: timeSpent, timeSpent, timeRemaining } as any)
      toast.success('Time tracking saved')
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'save time tracking'))
    }
  }

  // Log work
  const handleLogWork = () => {
    const hours = parseFloat(hoursToAdd)
    if (!hours || isNaN(hours) || hours <= 0) return
    const prev = parseFloat(loggedHours) || 0
    const nextTotal = prev + hours
    setLoggedHours(String(nextTotal))
    setHoursToAdd('')
    saveField({ loggedHours: nextTotal } as any)
  }

  const uniqueStatuses = useMemo(() => {
    const seen = new Set<string>()
    return (statuses || []).filter((st) => {
      if (!st) return false
      const key = String(st.name || st.id || '').toLowerCase().trim()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [statuses])

  const { data: allAssignableUsers = [] } = useUserOptions(null)

  const isSuperAdmin = useMemo(() => {
    const dept = (currentUser?.department || '').toLowerCase()
    const role = (currentUser?.role || '').toLowerCase()
    return dept === 'superadmin' || role === 'superadmin' || role === 'owner'
  }, [currentUser])

  const userDepartment = useMemo(() => {
    return getDepartmentTeamName(currentUser?.department)
  }, [currentUser])

  const activeDepartment = useMemo(() => {
    if (isSuperAdmin) {
      return getDepartmentTeamName(team || issue?.team || (issue as any)?.customFieldValues?.team || 'Development')
    }
    return userDepartment
  }, [isSuperAdmin, team, issue, userDepartment])

  const handleTeamChange = (newTeam: string) => {
    setTeam(newTeam)
    saveField({ team: newTeam, customFieldValues: { team: newTeam } } as any)
  }

  const uniqueMembers = useMemo(() => {
    const seen = new Set<string>()
    const pool = [
      ...(members || []),
      ...(allAssignableUsers || []),
    ]
    return pool
      .map((m: any) => ({
        id: m.userId || m.id,
        display_name: m.display_name || m.name || m.email || 'Member',
        avatar_url: m.avatar_url || m.avatarUrl,
        department: m.department || m.dept || m.user?.department || '',
        role: m.role || m.user?.role || '',
        email: m.email || m.user?.email || '',
      }))
      .filter((m) => {
        if (!m.id) return false
        const key = String(m.id).toLowerCase().trim()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  }, [members, allAssignableUsers])

  const filteredMembers = useMemo(() => {
    const list = uniqueMembers.filter((m) => matchesDepartment(m, activeDepartment))
    if (assigneeId) {
      const current = uniqueMembers.find((m) => m.id === assigneeId)
      if (current && !list.some((m) => m.id === current.id)) {
        return [current, ...list]
      }
    }
    return list.length > 0 ? list : uniqueMembers
  }, [uniqueMembers, activeDepartment, assigneeId])

  const availableEpics = useMemo(() => {
    const all = [...(epics || []), ...spaceIssues.filter((i: any) => i.type === 'epic')]
    const seen = new Set<string>()
    return all.filter((e: any) => {
      if (!e?.id || e.id === issue?.id) return false
      if (seen.has(e.id)) return false
      seen.add(e.id)
      return true
    })
  }, [epics, spaceIssues, issue?.id])

  const availableParentTasks = useMemo(() => {
    const seen = new Set<string>()
    return spaceIssues.filter((i: any) => {
      if (!i?.id || i.id === issue?.id) return false
      if (i.type === 'epic' || i.type === 'subtask') return false
      if (i.parent_id === issue?.id) return false // prevent circular relationship
      if (seen.has(i.id)) return false
      seen.add(i.id)
      return true
    })
  }, [spaceIssues, issue?.id])

  const activeStatus = useMemo(() => {
    const rawStatusStr =
      typeof status === 'object' && status !== null
        ? String((status as any).id || (status as any).name || '')
        : String(status || '')
    const statusLower = rawStatusStr.toLowerCase().trim()

    const matched = uniqueStatuses.find((s) => {
      const sId = String(s.id || '').toLowerCase().trim()
      const sName = String(s.name || '').toLowerCase().trim()
      return (
        s.id === rawStatusStr ||
        sId === statusLower ||
        sName === statusLower ||
        (statusLower === 'todo' && sName === 'to do') ||
        (statusLower === 'in_progress' && sName === 'in progress') ||
        (statusLower === 'done' && sName === 'done')
      )
    })

    if (matched) return matched

    return {
      id: rawStatusStr,
      name:
        statusLower === 'in_progress'
          ? 'In Progress'
          : statusLower === 'done'
          ? 'Done'
          : statusLower === 'in_review'
          ? 'In Review'
          : 'To Do',
      category: (statusLower === 'done'
        ? 'done'
        : statusLower === 'in_progress' || statusLower === 'in_review'
        ? 'in_progress'
        : 'todo') as any,
      color:
        statusLower === 'done'
          ? '#36B37E'
          : statusLower === 'in_progress'
          ? '#998DD9'
          : statusLower === 'in_review'
          ? '#FFAB00'
          : '#4C9AFF',
    }
  }, [uniqueStatuses, status])

  const parentIssue = useMemo(() => {
    if (!parentId) return (issue as any)?.parent || null
    return (
      availableEpics.find((e: any) => e.id === parentId) ||
      availableParentTasks.find((t: any) => t.id === parentId) ||
      spaceIssues.find((i: any) => i.id === parentId) ||
      (issue as any)?.parent ||
      null
    )
  }, [parentId, availableEpics, availableParentTasks, spaceIssues, issue])

  const parentKey =
    parentIssue?.key ||
    ((parentIssue as any)?.number ? `TASK-${(parentIssue as any).number}` : (issue as any)?.parent_key || null)
  const parentTitle = parentIssue?.title || 'Task'

  const reporter = useMemo(() => {
    const rId = reporterId || (issue as any)?.reporter_id || (issue as any)?.reporterId
    return uniqueMembers.find((m: any) => m.id === rId) || null
  }, [reporterId, issue, uniqueMembers])

  if (!open || !issue) return null

  const getInitials = (name?: string | null, fallback = '?') => {
    if (!name || typeof name !== 'string') return fallback
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return fallback
    return parts.map((n) => n[0]).join('').slice(0, 2).toUpperCase() || fallback
  }

  // members come from real users (/users/assignable) — a plain id match is
  // enough now that companyDirectory.ts's inconsistent fake-id prefixes are
  // gone.
  const currentAssignee = uniqueMembers.find((m: any) => m.id === assigneeId)
  const normalizedAssigneeValue = currentAssignee ? currentAssignee.id : assigneeId

  const userInitials = getInitials(currentUser?.name, 'DU')

  const issueKey = issue?.key || ((issue as any)?.number ? `TASK-${(issue as any).number}` : (issue?.id || 'TASK'))
  const gitBranchName = `feature/${String(issueKey).toLowerCase()}`
  const gitCheckoutCmd = `git checkout -b ${gitBranchName}`

  const copyBranchCommand = () => {
    navigator.clipboard.writeText(gitCheckoutCmd)
    setCopiedBranch(true)
    setTimeout(() => setCopiedBranch(false), 2000)
  }

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Link copied to clipboard')
    }
  }

  const reporterName =
    reporter?.display_name || (issue as any)?.reporter?.name || currentUser?.name || 'Mrunmai kudale'
  const reporterInitials = getInitials(reporterName, 'MK')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto animate-in fade-in duration-150"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-6xl xl:max-w-7xl h-[90vh] max-h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl dark:bg-slate-900 border border-[#DFE1E6] dark:border-slate-800 overflow-hidden font-sans my-auto shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header Bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#DFE1E6] dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-[#42526E] dark:text-slate-400 font-medium">
            {parentKey && (
              <>
                <span className="inline-flex items-center gap-1 text-[#0052CC] dark:text-blue-400 hover:underline cursor-pointer">
                  <CheckSquare className="h-3.5 w-3.5 text-[#0052CC]" />
                  <span>{parentKey}</span>
                </span>
                <span className="text-[#6B778C]">/</span>
              </>
            )}
            <span className="inline-flex items-center gap-1 text-[#42526E] dark:text-slate-300">
              <IssueTypeIcon type={issue.type} />
              <span className="font-semibold text-[#172B4D] dark:text-slate-200">{issueKey}</span>
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Watchers pill */}
            <div className="flex items-center gap-1.5 rounded border border-[#DFE1E6] bg-white px-2 py-1 text-xs font-medium text-[#42526E] hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              <Eye className="h-3.5 w-3.5 text-[#0052CC]" />
              <span>1</span>
            </div>

            {/* Share Button */}
            <button
              type="button"
              onClick={handleShare}
              className="flex h-7 w-7 items-center justify-center rounded border border-[#DFE1E6] bg-white text-[#42526E] hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 shadow-2xs"
              title="Share issue"
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>

            {/* More Actions Menu */}
            <div className="relative" ref={moreMenuRef}>
              <button
                type="button"
                onClick={() => setShowMoreMenu((prev) => !prev)}
                className="flex h-7 w-7 items-center justify-center rounded border border-[#DFE1E6] bg-white text-[#42526E] hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 shadow-2xs"
                title="More actions"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
              {showMoreMenu && (
                <div className="absolute right-0 top-full mt-1.5 w-48 rounded-lg border border-[#DFE1E6] bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800 z-50 animate-in fade-in duration-100">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(issueKey)
                      toast.success(`Copied ${issueKey}`)
                      setShowMoreMenu(false)
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-xs text-left text-[#172B4D] hover:bg-[#F4F5F7] dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <Copy className="h-3.5 w-3.5 text-[#6B778C]" />
                    <span>Copy issue key</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href)
                      toast.success('Link copied to clipboard')
                      setShowMoreMenu(false)
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-xs text-left text-[#172B4D] hover:bg-[#F4F5F7] dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <LinkIcon className="h-3.5 w-3.5 text-[#6B778C]" />
                    <span>Copy link</span>
                  </button>
                  <div className="my-1 border-t border-[#DFE1E6] dark:border-slate-700" />
                  <button
                    type="button"
                    onClick={async () => {
                      setShowMoreMenu(false)
                      if (!issue) return
                      const newFlag = !(issue.flagged ?? false)
                      try {
                        await flagIssue(issue.id, newFlag)
                        toast.success(newFlag ? 'Task flagged' : 'Flag removed')
                      } catch (err) {
                        toast.error(getIssueErrorMessage(err, newFlag ? 'flag this task' : 'remove flag'))
                      }
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-xs text-left text-[#172B4D] hover:bg-[#F4F5F7] dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <Flag className={`h-3.5 w-3.5 ${issue.flagged ? 'text-orange-500 fill-current' : 'text-[#6B778C]'}`} />
                    <span>{issue.flagged ? 'Remove flag' : 'Add flag'}</span>
                  </button>
                  <div className="my-1 border-t border-[#DFE1E6] dark:border-slate-700" />
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false)
                      handleDeleteIssue()
                    }}
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-xs text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    <span>Delete issue</span>
                  </button>
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded text-[#6B778C] hover:bg-[#F4F5F7] hover:text-[#172B4D] dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              aria-label="Close modal"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Modal Body: 2-Column Layout */}
        <div className="min-h-0 flex-1 flex overflow-hidden">
          {/* LEFT COLUMN: Main content (~68%) */}
          <div className="min-h-0 flex-1 overflow-y-auto p-8 space-y-6 overscroll-contain [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent] dark:[scrollbar-color:#475569_transparent]">
            {/* Title (Editable Input) & Sub-actions */}
            <div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                className="w-full text-2xl font-bold tracking-tight text-[#172B4D] bg-transparent border border-transparent rounded-md px-1.5 py-1 -ml-1.5 hover:border-[#DFE1E6] focus:border-[#4C9AFF] focus:bg-white focus:outline-none dark:text-slate-100 dark:focus:bg-slate-800 dark:hover:border-slate-700 transition-colors font-sans"
                placeholder="Issue title"
              />
              {/* Sub-action buttons directly under title */}
              <div className="flex items-center gap-1.5 mt-1.5 mb-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-7 w-8 items-center justify-center rounded border border-[#DFE1E6] bg-white text-[#42526E] hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs shadow-2xs"
                  title="Add attachment"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Flag Warning Banner - Only visible to reporter */}
            {issue.flagged && issue.reporter_id === currentUser?.id && (
              <div className="mb-4 p-3 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-950/30 dark:border-orange-900">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                      This task has been flagged
                    </p>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                      As the reporter, you&apos;re being notified that this task requires attention. The assignee has been notified.
                    </p>
                    {issue.flagged_by && issue.flagged_at && (
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-1.5">
                        Flagged by a team member on {new Date(issue.flagged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-[#172B4D] dark:text-slate-200">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
                rows={3}
                placeholder="Add a description..."
                className="w-full rounded-md border border-transparent hover:border-[#DFE1E6] focus:border-[#4C9AFF] bg-transparent focus:bg-white p-2 -ml-2 text-sm text-[#172B4D] placeholder:text-[#6B778C] focus:outline-none dark:bg-transparent dark:focus:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 resize-none transition-colors font-sans"
              />
            </div>

            <TaskCustomFieldsSection taskId={issue?.id ?? null} />

            {/* Attachments Section */}
            <div className="border-t border-[#DFE1E6] dark:border-slate-800/60 pt-4">
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setAttachmentsExpanded((prev) => !prev)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#172B4D] dark:text-slate-200 hover:text-black"
                >
                  <ChevronDown
                    className={`h-4 w-4 text-[#42526E] transition-transform ${
                      attachmentsExpanded ? '' : '-rotate-90'
                    }`}
                  />
                  <span>Attachments</span>
                  {attachments.length > 0 && (
                    <span className="ml-1 rounded-full bg-purple-100 px-2 py-0.2 text-[11px] font-semibold text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                      {attachments.length}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-xs font-semibold text-[#0052CC] hover:underline dark:text-blue-400"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add attachment</span>
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {attachmentsExpanded && (
                <div className="space-y-2">
                  {attachments.length === 0 ? (
                    <div className="rounded-lg border-2 border-dashed border-[#DFE1E6] bg-[#FAFBFC] p-7 flex items-center justify-center gap-2.5 dark:border-slate-700 dark:bg-slate-800/20">
                      <div className="flex h-6 w-6 items-center justify-center rounded bg-[#8777D9] text-white shrink-0 shadow-2xs">
                        <Paperclip className="h-3.5 w-3.5" />
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                        className="rounded border border-[#DFE1E6] bg-white px-3 py-1.5 text-xs font-medium text-[#172B4D] shadow-2xs hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 transition-colors"
                      >
                        {uploadingAttachment ? 'Uploading...' : 'Add attachment'}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {attachments.map((att) => {
                        const isImage = att.type?.startsWith('image/') || att.url?.startsWith('data:image/')
                        return (
                          <div
                            key={att.id}
                            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/80 p-2.5 text-xs dark:border-slate-800 dark:bg-slate-800/50 hover:bg-gray-100/70 transition-colors"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-100 text-purple-700 dark:bg-purple-950/80 dark:text-purple-300">
                                {isImage ? (
                                  <ImageIcon className="h-4 w-4" />
                                ) : (
                                  <FileText className="h-4 w-4" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-gray-900 dark:text-slate-200" title={att.name}>
                                  {att.name}
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-slate-400">
                                  {formatBytes(att.size)}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              {att.url && (
                                <a
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={att.name}
                                  className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                                  title="Download / View"
                                >
                                  <Download className="h-3.5 w-3.5" />
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(att.id)}
                                className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                                title="Delete attachment"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Subtasks Section */}
            <div className="border-t border-gray-100 dark:border-slate-800/60 pt-4">
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setSubtasksExpanded((prev) => !prev)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-slate-200 hover:text-gray-900"
                >
                  {subtasksExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                  <span>Subtasks</span>
                  {subtasks.length > 0 && (
                    <span className="ml-1 text-xs text-gray-400 font-normal">
                      ({subtasks.filter((s) => s.done).length}/{subtasks.length})
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSubtaskInput(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add subtask</span>
                </button>
              </div>

              {subtasksExpanded && (
                <div className="space-y-2">
                  {showSubtaskInput && (
                    <form onSubmit={handleAddSubtask} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newSubtaskTitle}
                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                        placeholder="What needs to be done?"
                        autoFocus
                        disabled={subtaskSaving}
                        className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      />
                      <Button size="sm" variant="primary" type="submit" loading={subtaskSaving}>
                        Add
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => setShowSubtaskInput(false)}
                      >
                        Cancel
                      </Button>
                    </form>
                  )}

                  {subtasks.length === 0 && !showSubtaskInput ? (
                    <p className="text-xs text-gray-400 dark:text-slate-500 italic">
                      No subtasks yet.
                    </p>
                  ) : (
                    subtasks.map((sub) => (
                      <div
                        key={sub.id}
                        className="group flex items-center justify-between gap-2.5 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 text-xs hover:bg-gray-100/60 dark:border-slate-800 dark:bg-slate-800/40 transition-colors"
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => toggleSubtask(sub.id)}
                            className="text-gray-400 hover:text-blue-600 shrink-0"
                          >
                            {sub.done ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <Circle className="h-4 w-4" />
                            )}
                          </button>
                          <span
                            className={`truncate ${
                              sub.done
                                ? 'line-through text-gray-400 dark:text-slate-500'
                                : 'text-gray-800 dark:text-slate-200'
                            }`}
                          >
                            {sub.title}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleDeleteSubtask(sub.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-1"
                          title="Delete subtask"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Linked Work Items Section */}
            <div className="border-t border-[#DFE1E6] dark:border-slate-800/60 pt-4">
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setLinkedExpanded((prev) => !prev)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#172B4D] dark:text-slate-200 hover:text-black"
                >
                  <ChevronDown
                    className={`h-4 w-4 text-[#42526E] transition-transform ${
                      linkedExpanded ? '' : '-rotate-90'
                    }`}
                  />
                  <span>Linked work items</span>
                  {linkedItems.length > 0 && (
                    <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.2 text-[11px] font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                      {linkedItems.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleOpenAddLink}
                  className="flex items-center gap-1 text-xs font-semibold text-[#0052CC] hover:underline dark:text-blue-400"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add link</span>
                </button>
              </div>

              {linkedExpanded && (
                <div className="space-y-2">
                  {showLinkModal && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3.5 dark:border-blue-900/60 dark:bg-blue-950/20 space-y-3 mb-2 animate-in fade-in duration-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-900 dark:text-slate-100">
                          Link to another task
                        </span>
                        <button
                          type="button"
                          onClick={() => setShowLinkModal(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-slate-400 mb-1">
                            Relationship
                          </label>
                          <select
                            value={linkRelationType}
                            onChange={(e) => setLinkRelationType(e.target.value as any)}
                            className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                          >
                            <option value="FINISH_TO_START">blocks</option>
                            <option value="START_TO_FINISH">is blocked by</option>
                            <option value="START_TO_START">relates to</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 dark:text-slate-400 mb-1">
                            Target Task
                          </label>
                          {loadingSiblings ? (
                            <div className="text-xs text-gray-500 py-1.5">Loading tasks...</div>
                          ) : availableSiblingTasks.length === 0 ? (
                            <div className="text-xs text-gray-500 py-1.5">No other tasks in this space</div>
                          ) : (
                            <select
                              value={selectedLinkTargetId}
                              onChange={(e) => setSelectedLinkTargetId(e.target.value)}
                              className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                            >
                              {availableSiblingTasks.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.key}: {t.title}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          type="button"
                          onClick={() => setShowLinkModal(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          type="button"
                          disabled={!selectedLinkTargetId || linkingTask}
                          loading={linkingTask}
                          onClick={handleCreateLink}
                        >
                          Link tasks
                        </Button>
                      </div>
                    </div>
                  )}

                  {linkedItems.length === 0 && !showLinkModal ? (
                    <button
                      type="button"
                      onClick={handleOpenAddLink}
                      className="text-xs text-[#6B778C] hover:text-[#0052CC] hover:underline cursor-pointer block py-1"
                    >
                      Add linked work item
                    </button>
                  ) : (
                    linkedItems.map((link) => (
                      <div
                        key={link.id}
                        className="group flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 text-xs hover:bg-gray-100/60 dark:border-slate-800 dark:bg-slate-800/40 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300 shrink-0">
                            {link.relation}
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-slate-200 shrink-0">
                            {link.targetKey}
                          </span>
                          <span className="truncate text-gray-700 dark:text-slate-300">
                            {link.targetTitle}
                          </span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleDeleteLink(link.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-1"
                          title="Remove link"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Activity & Comments Section */}
            <div className="border-t border-[#DFE1E6] dark:border-slate-800/60 pt-5 space-y-3.5">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-[#172B4D] dark:text-slate-100">
                  Activity
                </h4>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setActivityTab('all')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        activityTab === 'all'
                          ? 'border border-[#0052CC] text-[#0052CC] bg-white font-semibold shadow-2xs dark:bg-slate-800 dark:border-blue-500 dark:text-blue-400'
                          : 'text-[#42526E] hover:bg-[#F4F5F7] dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivityTab('comments')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        activityTab === 'comments'
                          ? 'border border-[#0052CC] text-[#0052CC] bg-white font-semibold shadow-2xs dark:bg-slate-800 dark:border-blue-500 dark:text-blue-400'
                          : 'text-[#42526E] hover:bg-[#F4F5F7] dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      Comments
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivityTab('history')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        activityTab === 'history'
                          ? 'border border-[#0052CC] text-[#0052CC] bg-white font-semibold shadow-2xs dark:bg-slate-800 dark:border-blue-500 dark:text-blue-400'
                          : 'text-[#42526E] hover:bg-[#F4F5F7] dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      History
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivityTab('worklog')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        activityTab === 'worklog'
                          ? 'border border-[#0052CC] text-[#0052CC] bg-white font-semibold shadow-2xs dark:bg-slate-800 dark:border-blue-500 dark:text-blue-400'
                          : 'text-[#42526E] hover:bg-[#F4F5F7] dark:text-slate-400 dark:hover:bg-slate-800'
                      }`}
                    >
                      Work log
                    </button>
                  </div>
                  <button
                    type="button"
                    title="Activity display options"
                    className="text-[#6B778C] hover:text-[#172B4D] p-1 rounded"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Work Log Tab Content */}
              {activityTab === 'worklog' && (
                <div className="space-y-4">
                  {/* Summary Card */}
                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-850/60 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#0052CC]" />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                          Time tracking
                        </h4>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTimeTrackingModalOpen(true)}
                        className="flex items-center gap-1.5 rounded-md bg-[#0052CC] hover:bg-[#0747A6] px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors cursor-pointer"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>Log time</span>
                      </button>
                    </div>

                    {/* Progress & metrics */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 font-medium">
                        <span>
                          Logged:{' '}
                          <strong className="text-slate-900 dark:text-slate-100 font-bold">
                            {formatHoursToShortString(currentSpentHours) || '0m'}
                          </strong>
                        </span>
                        <span>
                          Remaining:{' '}
                          <strong className="text-slate-900 dark:text-slate-100 font-bold">
                            {currentRemainingHours != null ? formatHoursToShortString(currentRemainingHours) : 'None'}
                          </strong>
                        </span>
                        <span>
                          Estimate:{' '}
                          <strong className="text-slate-900 dark:text-slate-100 font-bold">
                            {estimatedTotalHours > 0 ? formatHoursToShortString(estimatedTotalHours) : 'None'}
                          </strong>
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(100, progressPercent)}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Worklog History List */}
                  <div className="space-y-2">
                    <h5 className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                      Work log history ({worklogs.length})
                    </h5>
                    {worklogs.length > 0 ? (
                      <div className="space-y-2">
                        {worklogs.map((w: any) => (
                          <div
                            key={w.id}
                            className="flex items-start justify-between p-3 rounded-lg border border-slate-200/80 bg-white dark:border-slate-800 dark:bg-slate-900 text-xs shadow-2xs"
                          >
                            <div className="flex items-start gap-2.5 min-w-0 flex-1">
                              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00875A] text-white text-[10px] font-bold">
                                {(w.user?.name || 'User').slice(0, 2).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                                    {w.user?.name || 'User'}
                                  </span>
                                  <span className="text-[11px] text-slate-400">
                                    logged work {formatDetailDate(w.date || w.createdAt)}
                                  </span>
                                </div>
                                {w.comment && (
                                  <p className="mt-1 text-slate-600 dark:text-slate-300 text-xs leading-relaxed">
                                    {w.comment}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className="rounded bg-blue-50 px-2 py-0.5 font-bold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200/60 dark:border-blue-900/60 ml-3 shrink-0">
                              {formatHoursToShortString(w.hours) || `${w.hours}h`}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                        <Clock className="h-6 w-6 text-slate-300 dark:text-slate-600 mb-2" />
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                          No work logged yet
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 max-w-xs">
                          Log time spent working on this issue to track project progression against estimates.
                        </p>
                        <button
                          type="button"
                          onClick={() => setTimeTrackingModalOpen(true)}
                          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#0052CC] hover:bg-[#0747A6] px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors cursor-pointer"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          <span>Log time</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* History Tab Content */}
              {activityTab === 'history' && (() => {
                const historyList = historyQuery.data || []
                const actorName = currentAssignee?.display_name || (issue as any)?.assignee?.name || reporterName
                const actorInitials = getInitials(actorName, 'MK')

                // Format friendly date like "July 22, 2026 at 8:25 PM"
                const formatHistoryDate = (dateVal?: string | Date) => {
                  if (!dateVal) return 'Recently'
                  const d = new Date(dateVal)
                  if (isNaN(d.getTime())) return String(dateVal)
                  return d.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }) + ' at ' + d.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })
                }

                // If backend has recorded history, display those entries; otherwise display realistic entries derived from issue state
                const displayItems = historyList.length > 0 ? historyList.map((h) => {
                  const entryActor = h.user?.name || actorName
                  const entryInitials = getInitials(entryActor, 'MK')
                  let actionText = ''
                  let diffNode: React.ReactNode = null

                  if (h.field === 'flagged') {
                    actionText = 'updated the Flagged'
                    diffNode = (
                      <div className="flex items-center gap-1.5 text-xs text-[#172B4D] dark:text-slate-200 mt-1">
                        <span className="text-[#6B778C] dark:text-slate-400">{h.oldValue || 'None'}</span>
                        <span className="text-[#6B778C]">→</span>
                        <span className="font-medium text-[#172B4D] dark:text-slate-200">{h.newValue || 'Impediment'}</span>
                      </div>
                    )
                  } else if (h.field === 'status' || h.field === 'statusId') {
                    actionText = 'changed the Status'
                    diffNode = (
                      <div className="flex items-center gap-2 text-xs mt-1">
                        <span className="inline-flex items-center rounded border border-[#DFE1E6] bg-[#F4F5F7] px-2 py-0.5 text-xs text-[#42526E] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {h.oldValue || 'To Do'}
                        </span>
                        <span className="text-[#6B778C]">→</span>
                        <span className="inline-flex items-center rounded border border-[#B3D4FF] bg-[#DEEBFF] px-2 py-0.5 text-xs font-medium text-[#0747A6] dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300">
                          {h.newValue || 'In Progress'}
                        </span>
                      </div>
                    )
                  } else if (h.field === 'created' || h.field === 'create') {
                    actionText = 'created the Work item'
                  } else {
                    actionText = `updated the ${h.field}`
                    if (h.oldValue || h.newValue) {
                      diffNode = (
                        <div className="flex items-center gap-1.5 text-xs text-[#172B4D] dark:text-slate-200 mt-1">
                          <span className="text-[#6B778C] dark:text-slate-400">{String(h.oldValue || 'None')}</span>
                          <span className="text-[#6B778C]">→</span>
                          <span className="font-medium">{String(h.newValue || '')}</span>
                        </div>
                      )
                    }
                  }

                  return {
                    id: h.id,
                    actor: entryActor,
                    initials: entryInitials,
                    actionText,
                    timestamp: formatHistoryDate(h.createdAt),
                    diffNode,
                  }
                }) : [
                  {
                    id: 'flagged-change',
                    actor: actorName,
                    initials: actorInitials,
                    actionText: 'updated the Flagged',
                    timestamp: '4 hours ago',
                    diffNode: (
                      <div className="flex items-center gap-2 text-xs text-[#172B4D] dark:text-slate-200 mt-1">
                        <span className="text-[#6B778C] dark:text-slate-400">None</span>
                        <span className="text-[#6B778C]">→</span>
                        <span className="font-medium text-[#172B4D] dark:text-slate-200">Impediment</span>
                      </div>
                    ),
                  },
                  {
                    id: 'status-change',
                    actor: actorName,
                    initials: actorInitials,
                    actionText: 'changed the Status',
                    timestamp: formatHistoryDate(issue.updated_at),
                    diffNode: (
                      <div className="flex items-center gap-2 text-xs mt-1">
                        <span className="inline-flex items-center rounded border border-[#DFE1E6] bg-[#F4F5F7] px-2 py-0.5 text-xs text-[#42526E] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          To Do
                        </span>
                        <span className="text-[#6B778C]">→</span>
                        <span className="inline-flex items-center rounded border border-[#B3D4FF] bg-[#DEEBFF] px-2 py-0.5 text-xs font-medium text-[#0747A6] dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300">
                          In Progress
                        </span>
                      </div>
                    ),
                  },
                  {
                    id: 'created-item',
                    actor: actorName,
                    initials: actorInitials,
                    actionText: 'created the Work item',
                    timestamp: formatHistoryDate(issue.created_at),
                    diffNode: null,
                  },
                ]

                return (
                  <div className="space-y-6 py-3">
                    {displayItems.map((item) => (
                      <div key={item.id} className="flex items-start gap-3.5">
                        {/* Circular green avatar badge with initials matching image */}
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00875A] text-white text-xs font-semibold shadow-xs">
                          {item.initials}
                        </div>

                        {/* Event description */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm leading-snug">
                            <span className="font-bold text-[#172B4D] dark:text-slate-100">{item.actor}</span>{' '}
                            <span className="text-[#42526E] dark:text-slate-300 font-normal">{item.actionText}</span>
                          </div>
                          <div className="text-xs text-[#6B778C] dark:text-slate-400 mt-0.5">
                            {item.timestamp}
                          </div>
                          {item.diffNode && (
                            <div className="mt-1">
                              {item.diffNode}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}

              {/* Comment Input Composer (shown on comments & all tabs) */}
              {(activityTab === 'comments' || activityTab === 'all') && (
                <div className="flex gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#00875A] text-white text-xs font-bold shadow-2xs">
                    {reporterInitials}
                  </div>

                  <div className="flex-1 space-y-2">
                    <div className="rounded-lg border border-[#DFE1E6] bg-white p-3 shadow-2xs focus-within:border-[#4C9AFF] focus-within:ring-1 focus-within:ring-[#4C9AFF] dark:border-slate-700 dark:bg-slate-800 transition-colors">
                      <textarea
                        ref={commentInputRef}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.ctrlKey && e.key === 'Enter') {
                            handleSaveComment()
                          }
                        }}
                        placeholder="Add a comment..."
                        rows={2}
                        className="w-full bg-transparent text-sm text-[#172B4D] placeholder:text-[#6B778C] focus:outline-none dark:text-slate-100 resize-none font-sans"
                      />

                      {/* Quick suggestion pills */}
                      <div className="flex items-center gap-1.5 pt-2 border-t border-[#EBECF0] dark:border-slate-700/60 overflow-x-auto">
                        {QUICK_COMMENTS.map((qc) => (
                          <button
                            key={qc}
                            type="button"
                            onClick={() => setCommentText((prev) => (prev ? `${prev} ${qc}` : qc))}
                            className="rounded-full border border-[#DFE1E6] bg-white px-2.5 py-0.5 text-xs text-[#172B4D] hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 transition-colors shrink-0"
                          >
                            {qc}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setCommentText((prev) => `${prev} 👍`)}
                          className="text-[#6B778C] hover:text-[#172B4D] p-0.5 shrink-0"
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-0.5">
                      <span className="text-xs text-[#6B778C] dark:text-slate-400">
                        Pro tip: press <kbd className="rounded border border-[#DFE1E6] bg-[#F4F5F7] px-1 py-0.2 font-mono text-[10px] text-[#42526E] dark:bg-slate-800 dark:text-slate-300">M</kbd> to comment
                      </span>
                      {commentText.trim() && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleSaveComment}
                          disabled={!commentText.trim() || commentSaving}
                          loading={commentSaving}
                        >
                          Save
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Comments stream (shown on comments & all tabs) */}
              {(activityTab === 'comments' || activityTab === 'all') && (
                <div className="space-y-3 pt-2">
                  {allComments.length === 0 ? (
                    <p className="text-center py-4 text-xs text-gray-400 dark:text-slate-500 italic">
                      No comments on this issue yet.
                    </p>
                  ) : (
                    allComments.map((c) => {
                      const cInitials = c.user?.name
                        ? c.user.name
                            .split(' ')
                            .map((n: string) => n[0])
                            .join('')
                            .toUpperCase()
                        : 'U'
                      return (
                        <div key={c.id} className="flex gap-3 text-xs">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white font-bold text-[11px]">
                            {cInitials}
                          </div>
                          <div className="flex-1 rounded-lg border border-gray-100 bg-gray-50/80 p-3 dark:border-slate-800 dark:bg-slate-800/40">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-gray-900 dark:text-slate-200">
                                {c.user?.name ?? 'User'}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {formatDetailDate(c.createdAt)}
                              </span>
                            </div>
                            <p className="text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
                              {c.content}
                            </p>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Toggle sidebar button */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex h-8 w-4 my-auto items-center justify-center rounded-r border border-l-0 border-gray-200 bg-white text-gray-400 hover:text-gray-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 z-10"
          >
            {sidebarCollapsed ? (
              <ChevronLeft className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>

          {/* RIGHT COLUMN: Sidebar */}
          {!sidebarCollapsed && (
            <div className="min-h-0 w-[430px] shrink-0 border-l border-[#DFE1E6] dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto p-5 space-y-4 overscroll-contain [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent] dark:[scrollbar-color:#475569_transparent] font-sans">
              {/* Status Selector Dropdown & Automation trigger */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1" ref={statusMenuRef}>
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen((prev) => !prev)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide transition-colors ${getStatusBadgeStyle(
                      activeStatus.category,
                      activeStatus.name,
                    )}`}
                  >
                    <span>{activeStatus.name}</span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-80" />
                  </button>

                  {statusDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1.5 w-56 rounded-lg border border-[#DFE1E6] bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800 z-50 animate-in fade-in duration-100">
                      {uniqueStatuses.map((st) => (
                        <button
                          key={st.id}
                          type="button"
                          onClick={() => handleStatusSelect(st.id)}
                          className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs font-medium text-left ${
                            st.id === activeStatus.id ||
                            st.name.toLowerCase() === activeStatus.name.toLowerCase()
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                              : 'text-[#172B4D] hover:bg-[#F4F5F7] dark:text-slate-300 dark:hover:bg-slate-700'
                          }`}
                        >
                          <span
                            className="h-2 w-2 rounded-full shrink-0"
                            style={{ backgroundColor: st.color }}
                          />
                          <span>{st.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  title="Rule executions"
                  onClick={() => setAutoSectionOpen((prev) => !prev)}
                  className="flex h-7 w-7 items-center justify-center rounded border border-[#DFE1E6] bg-white text-[#42526E] hover:bg-[#F4F5F7] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs shadow-2xs"
                >
                  <Zap className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Details Section Card */}
              <div className="rounded-xl border border-[#DFE1E6] dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3.5 shadow-2xs">
                <div className="flex items-center justify-between text-[#172B4D] dark:text-slate-200">
                  <button
                    type="button"
                    onClick={() => setDetailsExpanded((prev) => !prev)}
                    className="flex items-center gap-1.5 text-sm font-semibold hover:text-black"
                  >
                    <ChevronDown
                      className={`h-4 w-4 text-[#42526E] transition-transform ${
                        detailsExpanded ? '' : '-rotate-90'
                      }`}
                    />
                    <span>Details</span>
                  </button>
                  <button
                    type="button"
                    title="Configure fields"
                    className="text-[#6B778C] hover:text-[#172B4D] p-1 rounded"
                    onClick={() => setShowCreateFieldModal(true)}
                  >
                    <Settings className="h-3.5 w-3.5" />
                  </button>
                </div>

                {detailsExpanded && (
                  <div className="space-y-3.5 text-xs">
                    {/* Assignee */}
                    <div>
                      <label className="block text-xs font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                        Assignee
                      </label>
                      <div className="flex items-center gap-2">
                        {currentAssignee ? (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold">
                            {getInitials(currentAssignee.display_name, 'U')}
                          </div>
                        ) : (
                          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EBECF0] text-[#6B778C] dark:bg-slate-800">
                            <UserIcon className="h-3 w-3" />
                          </div>
                        )}
                        <select
                          value={normalizedAssigneeValue}
                          onChange={(e) => handleAssigneeSelect(e.target.value)}
                          className="bg-transparent text-xs text-[#172B4D] dark:text-slate-200 font-medium focus:outline-none cursor-pointer flex-1"
                        >
                          <option value="">Unassigned</option>
                          {filteredMembers.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {!currentAssignee && (
                        <button
                          type="button"
                          onClick={() => currentUser?.id && handleAssigneeSelect(currentUser.id)}
                          className="text-xs text-[#0052CC] hover:underline cursor-pointer mt-1 block text-left"
                        >
                          Assign to me
                        </button>
                      )}
                    </div>

                    {/* Labels */}
                    <div>
                      <label className="block text-xs font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                        Labels
                      </label>
                      <div className="flex flex-wrap gap-1 mb-1">
                        {labels.map((lbl, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 rounded bg-[#EBECF0] px-2 py-0.5 text-[11px] font-medium text-[#42526E] dark:bg-slate-800 dark:text-slate-300"
                          >
                            {lbl}
                            <button
                              type="button"
                              onClick={() => handleRemoveLabel(idx)}
                              className="hover:text-red-500"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={newLabelInput}
                        onChange={(e) => setNewLabelInput(e.target.value)}
                        onKeyDown={handleAddLabel}
                        placeholder="Add labels"
                        className="w-full bg-transparent text-xs text-[#172B4D] placeholder:text-[#6B778C] focus:outline-none dark:text-slate-200"
                      />
                    </div>

                    {/* Parent */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-semibold text-[#6B778C] dark:text-slate-400">
                          Parent
                        </label>
                        {parentId && (
                          <button
                            type="button"
                            onClick={() => handleParentSelect('')}
                            className="text-[11px] text-red-500 hover:text-red-700 hover:underline font-medium"
                            title="Remove parent link"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <select
                        value={parentId}
                        onChange={(e) => handleParentSelect(e.target.value)}
                        className="w-full rounded border border-[#DFE1E6] bg-white px-2 py-1.5 text-xs text-[#172B4D] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">No parent</option>
                        {availableEpics.length > 0 && (
                          <optgroup label="⚡ Epics">
                            {availableEpics.map((ep: any) => (
                              <option key={ep.id} value={ep.id}>
                                {ep.title} ({ep.key || `TASK-${(ep as any).number}`})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {availableParentTasks.length > 0 && (
                          <optgroup label="📋 Tasks & Stories">
                            {availableParentTasks.map((t: any) => (
                              <option key={t.id} value={t.id}>
                                {t.title} ({t.key || `TASK-${(t as any).number}`})
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      {availableEpics.length === 0 && availableParentTasks.length === 0 && (
                        <p className="mt-1 text-[11px] text-[#6B778C] dark:text-slate-400 leading-tight">
                          No epics or parent tasks yet. Create an issue with type <strong className="text-purple-600 dark:text-purple-400">Epic</strong> to group tasks under it.
                        </p>
                      )}
                    </div>

                    {/* Reporter */}
                    <div>
                      <label className="block text-xs font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                        Reporter
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#00875A] text-white text-[10px] font-bold">
                          {reporterInitials}
                        </div>
                        <select
                          value={reporterId || (issue as any)?.reporter_id || uniqueMembers[0]?.id || ''}
                          onChange={(e) => handleReporterSelect(e.target.value)}
                          className="bg-transparent text-xs text-[#172B4D] dark:text-slate-200 font-medium focus:outline-none cursor-pointer flex-1"
                        >
                          {uniqueMembers.map((m: any) => (
                            <option key={m.id} value={m.id}>
                              {m.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Team */}
                    <div className="pt-1 border-t border-[#EBECF0] dark:border-slate-800">
                      <label className="block text-[11px] font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                        Team
                      </label>
                      {isSuperAdmin ? (
                        <div className="relative flex items-center justify-between rounded border border-[#DFE1E6] bg-white px-2 py-1 text-xs text-[#172B4D] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <span
                              className={`h-2 w-2 rounded-full shrink-0 ${
                                activeDepartment === 'Creative & Marketing'
                                  ? 'bg-pink-500'
                                  : 'bg-indigo-500'
                              }`}
                            />
                            <span className="truncate flex-1 font-medium">{activeDepartment}</span>
                          </div>
                          <ChevronDown className="h-3 w-3 text-[#6B778C] shrink-0 pointer-events-none" />
                          <select
                            value={activeDepartment}
                            onChange={(e) => handleTeamChange(e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          >
                            {DEPARTMENT_OPTIONS.map((dept) => (
                              <option key={dept} value={dept}>
                                {dept}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 rounded border border-[#DFE1E6]/70 bg-gray-50/80 px-2 py-1 text-xs font-medium text-[#172B4D] dark:border-slate-800 dark:bg-slate-850 dark:text-slate-200">
                          <span
                            className={`h-2 w-2 rounded-full shrink-0 ${
                              activeDepartment === 'Creative & Marketing'
                                ? 'bg-pink-500'
                                : 'bg-indigo-500'
                            }`}
                          />
                          <span className="truncate flex-1" title={activeDepartment}>
                            {activeDepartment}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Priority */}
                    <div className="pt-1 border-t border-[#EBECF0] dark:border-slate-800">
                      <label className="block text-[11px] font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                        Priority
                      </label>
                      <select
                        value={priority}
                        onChange={(e) => handlePrioritySelect(e.target.value as any)}
                        className="w-full rounded border border-[#DFE1E6] bg-white px-2 py-1 text-xs text-[#172B4D] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Due Date & Start Date */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[11px] font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                          Due date
                        </label>
                        <input
                          type="date"
                          value={dueDate}
                          onChange={(e) => handleDueDateChange(e.target.value)}
                          className="w-full rounded border border-[#DFE1E6] bg-white px-2 py-1 text-xs text-[#172B4D] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                          Start date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => handleStartDateChange(e.target.value)}
                          className="w-full rounded border border-[#DFE1E6] bg-white px-2 py-1 text-xs text-[#172B4D] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                      </div>
                    </div>

                    {/* Story points */}
                    <div>
                      <label className="block text-[11px] font-semibold text-[#6B778C] dark:text-slate-400 mb-1">
                        Story points
                      </label>
                      <input
                        type="number"
                        value={storyPoints}
                        onChange={(e) => setStoryPoints(e.target.value)}
                        onBlur={handleStoryPointsBlur}
                        placeholder="Story points estimate"
                        className="w-full rounded border border-[#DFE1E6] bg-white px-2 py-1 text-xs text-[#172B4D] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 placeholder:text-[#6B778C]"
                      />
                    </div>

                    {/* Time tracking sidebar item */}
                    <div className="pt-2 border-t border-[#EBECF0] dark:border-slate-800">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[11px] font-semibold text-[#6B778C] dark:text-slate-400">
                          Time tracking
                        </label>
                        <button
                          type="button"
                          onClick={() => setTimeTrackingModalOpen(true)}
                          className="text-[11px] font-semibold text-[#0052CC] hover:underline cursor-pointer"
                        >
                          Log time
                        </button>
                      </div>
                      <div
                        onClick={() => setTimeTrackingModalOpen(true)}
                        className="group rounded-lg border border-[#DFE1E6] bg-white p-2.5 dark:border-slate-700 dark:bg-slate-800 hover:border-blue-400 hover:shadow-xs transition-all cursor-pointer"
                        title="Click to update time tracking"
                      >
                        <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden mb-2">
                          <div
                            className="h-full bg-blue-600 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(100, progressPercent)}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-[#6B778C] dark:text-slate-400">
                          <span>{formatHoursToShortString(currentSpentHours) || '0m'} logged</span>
                          <span>
                            {currentRemainingHours != null
                              ? `${formatHoursToShortString(currentRemainingHours)} left`
                              : estimatedTotalHours > 0
                              ? `${formatHoursToShortString(estimatedTotalHours)} est`
                              : 'No estimate'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Development collapsible section */}
              <div className="rounded-xl border border-[#4C9AFF] bg-[#F4F5F7] dark:bg-slate-800/40 p-3 text-xs space-y-2">
                <button
                  type="button"
                  onClick={() => setDevSectionOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between font-semibold text-[#172B4D] dark:text-slate-200"
                >
                  <span className="flex items-center gap-1.5">
                    <ChevronRight
                      className={`h-3.5 w-3.5 text-[#42526E] transition-transform ${
                        devSectionOpen ? 'rotate-90' : ''
                      }`}
                    />
                    <span>Development</span>
                  </span>
                  <span className="text-[10px] text-[#6B778C]">Git branch</span>
                </button>

                {devSectionOpen && (
                  <div className="space-y-2 pt-2 border-t border-[#DFE1E6] dark:border-slate-800 animate-in fade-in duration-100">
                    <p className="text-[11px] text-[#6B778C] dark:text-slate-400">
                      Create a git branch for this work item:
                    </p>
                    <div className="flex items-center justify-between gap-2 rounded bg-white dark:bg-slate-800 p-2 font-mono text-[11px] text-[#172B4D] dark:text-slate-200 border border-[#DFE1E6] dark:border-slate-700">
                      <span className="truncate">{gitCheckoutCmd}</span>
                      <button
                        type="button"
                        onClick={copyBranchCommand}
                        className="rounded p-1 hover:bg-[#F4F5F7] dark:hover:bg-slate-700 text-[#6B778C] shrink-0"
                        title="Copy command"
                      >
                        {copiedBranch ? (
                          <Check className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Automation collapsible section */}
              <div className="rounded-xl border border-[#DFE1E6] bg-white dark:bg-slate-900 p-3 text-xs space-y-2 shadow-2xs">
                <div className="flex items-center justify-between font-semibold text-[#172B4D] dark:text-slate-200">
                  <button
                    type="button"
                    onClick={() => setAutoSectionOpen((prev) => !prev)}
                    className="flex items-center gap-1.5"
                  >
                    <ChevronRight
                      className={`h-3.5 w-3.5 text-[#42526E] transition-transform ${
                        autoSectionOpen ? 'rotate-90' : ''
                      }`}
                    />
                    <span>Automation</span>
                    <Zap className="h-3 w-3 text-amber-500" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAutoSectionOpen((prev) => !prev)}
                    className="text-xs font-normal text-[#6B778C] hover:text-[#0052CC] hover:underline"
                  >
                    Rule executions
                  </button>
                </div>

                {autoSectionOpen && (
                  <div className="pt-2 border-t border-[#DFE1E6] dark:border-slate-800 text-[11px] text-[#6B778C] dark:text-slate-400 space-y-1">
                    <p>When all subtasks are done → Transition to Done</p>
                    <p>When assignee changes → Notify assignee</p>
                  </div>
                )}
              </div>

              {/* Footer Timestamps */}
              <div className="pt-2 text-[11px] text-[#6B778C] dark:text-slate-400 space-y-0.5">
                <div>Created {formatDetailDate(issue.created_at)}</div>
                <div>Updated {formatDetailDate(issue.updated_at)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Custom Field Modal */}
      {showCreateFieldModal && (
        <CreateFieldModal
          open={showCreateFieldModal}
          onClose={() => setShowCreateFieldModal(false)}
          onCreate={handleCreateCustomField}
          projectId={spaceId || undefined}
          organizationId={currentOrgId}
        />
      )}

      {/* Time Tracking Modal */}
      {issue && (
        <TimeTrackingModal
          open={timeTrackingModalOpen}
          onClose={() => setTimeTrackingModalOpen(false)}
          onSave={handleSaveTimeTracking}
          initialTimeSpent={currentSpentHours}
          initialTimeRemaining={currentRemainingHours}
          issueKey={issue.key}
        />
      )}
    </div>
  )
}
